import { useEffect, useRef, useState } from "react";

/** Homepage stats. Numbers to be re-verified by the data team (plus a Parent Builder
 *  stat to add) — edit here and both the card and the screen-reader list update. */
export const stats = [
  { value: "48,422", label: "Total Builders", tone: "bg-hxp-red-dark text-white" },
  { value: "28", label: "Years", tone: "bg-foreground text-background" },
  { value: "73", label: "Locations", tone: "bg-hxp-cream-2 text-foreground" },
  { value: "40", label: "Countries", tone: "bg-hxp-olive text-white" },
];

const FLIP_EVERY_MS = 3200;

/** "By the numbers" as ONE card that flips top-to-bottom to the next stat every few
 *  seconds (Steve, Oct 25: "one card that flips vertically to show the different stats").
 *  Two faces: the hidden face is pre-loaded with the next stat, then the card turns 180°.
 *  Pauses on hover; static (first stat + list) for reduced motion. */
const StatFlipCards = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [turns, setTurns] = useState(0);
  const [running, setRunning] = useState(false);
  const [hover, setHover] = useState(false);

  // Start only once the card is on screen.
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setRunning(true);
          obs.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!running || hover) return;
    const id = window.setInterval(() => setTurns((t) => t + 1), FLIP_EVERY_MS);
    return () => window.clearInterval(id);
  }, [running, hover]);

  const n = stats.length;
  // Face A shows on even turns, face B on odd; the hidden face holds the upcoming stat.
  const a = stats[(turns % 2 === 0 ? turns : turns + 1) % n];
  const b = stats[(turns % 2 === 1 ? turns : turns + 1) % n];

  const face = (s: (typeof stats)[number], extra: string) => (
    <div
      className={`absolute inset-0 flex flex-col justify-end rounded-sm p-6 shadow-sm [backface-visibility:hidden] md:p-8 ${s.tone} ${extra}`}
    >
      <span className="font-heading text-6xl leading-none sm:text-7xl md:text-8xl">{s.value}</span>
      <span className="btn-caps mt-3 text-xs tracking-[0.16em] opacity-80 md:text-sm">{s.label}</span>
    </div>
  );

  return (
    <div
      ref={ref}
      className="relative mx-auto w-full max-w-[420px] [perspective:1400px] lg:max-w-none"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div
        aria-hidden="true"
        className="relative aspect-[5/4] w-full [transform-style:preserve-3d]"
        style={{
          transform: `rotateX(${turns * -180}deg)`,
          transition: "transform 0.85s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        {face(a, "")}
        {face(b, "[transform:rotateX(180deg)]")}
      </div>
      {/* every stat, for screen readers and no-JS */}
      <ul className="sr-only">
        {stats.map((s) => (
          <li key={s.label}>
            {s.value} {s.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default StatFlipCards;
